/**
 * AttachmentUploader — RIGHT_INSP tool for managing downloadable resources.
 *
 * Applies to every scene type so coaches can attach PDFs, datasets, code
 * samples, and other files. Learners get in-app Document Viewer previews
 * (PDF / Markdown / sheet) plus download of the original.
 *
 * Uploads go through POST /scenario-os/coach/attachments/upload and land
 * in domain_metadata.attachments[]. Deletions hit DELETE
 * /scenario-os/coach/attachments/{scene_id}/{attachment_id}.
 */
import React, { useCallback, useRef, useState } from 'react';

import { DesignerTool } from '../DesignerTool';
import { useDesigner } from '../DesignerContext';
import { always } from '../predicates';
import DocumentViewer from '../../components/scenes/DocumentViewer';

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentUploaderPanel() {
  const { scene, setDomainMetadata } = useDesigner();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [viewing, setViewing] = useState(null);

  const meta = scene?.domain_metadata || {};
  const attachments = meta.attachments || [];
  const sceneId = scene?.scene_id;
  const domainId = scene?.domain_id;

  const handleUpload = useCallback(async () => {
    const file = fileRef.current?.files?.[0];
    if (!file || !sceneId) return;

    setUploading(true);
    setError('');

    const token = localStorage.getItem('scenario_os_token');
    const form = new FormData();
    form.append('file', file);
    form.append('domain_id', domainId || '');
    form.append('scene_id', sceneId);
    form.append('title', title || file.name);
    form.append('description', description);

    try {
      const res = await fetch('/scenario-os/coach/attachments/upload', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Upload failed (${res.status})`);
      }
      const att = await res.json();
      setDomainMetadata({ attachments: [...attachments, att] });
      setTitle('');
      setDescription('');
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [sceneId, domainId, title, description, attachments, setDomainMetadata]);

  const handleDelete = useCallback(async (attachmentId) => {
    const token = localStorage.getItem('scenario_os_token');
    try {
      await fetch(`/scenario-os/coach/attachments/${sceneId}/${attachmentId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setDomainMetadata({
        attachments: attachments.filter((a) => a.attachment_id !== attachmentId),
      });
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  }, [sceneId, attachments, setDomainMetadata]);

  if (!scene) return null;

  const btnStyle = {
    width: '100%',
    padding: '0.4rem',
    borderRadius: '5px',
    border: '1px solid #2d3748',
    background: '#1e2130',
    color: '#e2e8f0',
    cursor: uploading ? 'wait' : 'pointer',
    fontSize: '0.8125rem',
    opacity: uploading ? 0.6 : 1,
  };

  return (
    <div className="designer-tool" data-tool-id="platform.attachment_uploader">
      <h3 className="designer-tool__heading">
        {'\u{1F4CE}'} Attachments ({attachments.length})
      </h3>

      {attachments.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          {attachments.map((att) => (
            <div
              key={att.attachment_id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.4rem 0.5rem',
                marginBottom: '0.25rem',
                border: '1px solid #2d3748',
                borderRadius: '5px',
                background: '#0f1117',
                fontSize: '0.78rem',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 500, color: '#e2e8f0',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {att.title || att.filename}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                  {formatSize(att.size_bytes)}
                  {att.mime_type && ` \u00B7 ${att.mime_type.split('/').pop()}`}
                  {att.viewer_kind && ` \u00B7 ${att.viewer_kind}`}
                  {att.conversion_status === 'failed' && ' \u00B7 preview failed'}
                  {att.conversion_status === 'ok' && att.viewer_kind && att.viewer_kind !== 'download' && ' \u00B7 preview ready'}
                </div>
              </div>
              {att.viewer_kind && att.viewer_kind !== 'download' && (
                <button
                  type="button"
                  onClick={() => setViewing(att)}
                  style={{
                    background: '#1d4ed8', border: 'none', color: '#fff',
                    cursor: 'pointer', fontSize: '0.72rem', flexShrink: 0,
                    borderRadius: 4, padding: '2px 8px',
                  }}
                >
                  View
                </button>
              )}
              <button
                type="button"
                onClick={() => handleDelete(att.attachment_id)}
                style={{
                  background: 'none', border: 'none', color: '#ef4444',
                  cursor: 'pointer', fontSize: '0.8rem', flexShrink: 0,
                }}
                aria-label={`Remove ${att.title || att.filename}`}
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="designer-tool__row">
        <label className="designer-tool__label">Title (optional)</label>
        <input
          type="text"
          className="designer-tool__input"
          value={title}
          placeholder="Display name for the file"
          aria-label="Attachment title"
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="designer-tool__row">
        <label className="designer-tool__label">Description (optional)</label>
        <input
          type="text"
          className="designer-tool__input"
          value={description}
          placeholder="Brief description"
          aria-label="Attachment description"
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="designer-tool__row">
        <label className="designer-tool__label">File</label>
        <input
          ref={fileRef}
          type="file"
          aria-label="Choose file to upload"
          style={{
            fontSize: '0.78rem', color: '#94a3b8',
            width: '100%', marginBottom: '0.35rem',
          }}
        />
      </div>

      {error && (
        <div style={{ color: '#ef4444', fontSize: '0.75rem', marginBottom: '0.35rem' }}>
          {error}
        </div>
      )}

      <button
        type="button"
        disabled={uploading}
        onClick={handleUpload}
        style={btnStyle}
      >
        {uploading ? 'Uploading\u2026' : '+ Upload attachment'}
      </button>
      <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: '0.45rem', lineHeight: 1.4 }}>
        Original is always kept. Office files (doc/docx/ppt/pptx) get a LibreOffice PDF preview for in-app View;
        Markdown and Excel get native previews. Conversion is best-effort.
      </div>
      {viewing && sceneId && (
        <DocumentViewer
          sceneId={sceneId}
          attachment={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

const attachmentUploader = new DesignerTool({
  id:         'platform.attachment_uploader',
  label:      'Attachments',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: always,
  Component:  AttachmentUploaderPanel,
  weight:     90,
  helpText:   'Upload handouts (PDF/DOCX/PPTX/MD/XLSX). Original stored; in-app Document Viewer shows PDF/Markdown/sheet previews.',
});

export default attachmentUploader;
